import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
  IconButton,
  Alert
} from '@mui/material';
import {
  BugReport as BugReportIcon,
  Storage as StorageIcon,
  Refresh as RefreshIcon,
  Code as CodeIcon,
  Webhook as WebhookIcon,
  Close as CloseIcon,
  BarChart as BarChartIcon,
  Send as SendIcon,
  Database as DatabaseIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import WebhookTester from './settings/WebhookTester';
import WebhookStatsDashboard from './settings/WebhookStatsDashboard';
import SimpleWebhookTester from './SimpleWebhookTester';
import DatabaseManagement from './settings/DatabaseManagement';
import api from '../utils/api';

const DeveloperOptions = ({ open, onClose }) => {
  const [selectedSection, setSelectedSection] = useState(null);
  const [webhookTesterOpen, setWebhookTesterOpen] = useState(false);
  const [localWebhookTesterOpen, setLocalWebhookTesterOpen] = useState(false);
  const [webhookStatsOpen, setWebhookStatsOpen] = useState(false);
  const [simpleWebhookTesterOpen, setSimpleWebhookTesterOpen] = useState(false);
  const [databaseManagementOpen, setDatabaseManagementOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await api.get('/auth/me');
        setCurrentUser(response.data);
        setIsAdmin(['admin', 'superadmin'].includes(response.data.role));
      } catch (err) {
        console.error('Errore nel recupero dei dati utente:', err);
        setIsAdmin(false);
      }
    };
    
    fetchCurrentUser();
  }, []);

  const handleWebhookTesterOpen = () => {
    setWebhookTesterOpen(true);
    onClose();
  };

  const handleLocalWebhookTesterOpen = () => {
    setLocalWebhookTesterOpen(true);
    onClose();
  };
  
  const handleWebhookStatsOpen = () => {
    setWebhookStatsOpen(true);
    onClose();
  };
  
  const handleSimpleWebhookTesterOpen = () => {
    setSimpleWebhookTesterOpen(true);
    onClose();
  };
  
  const handleDatabaseManagementOpen = () => {
    setDatabaseManagementOpen(true);
    onClose();
  };

  const handleTestDatabase = () => {
    console.log('Test database');
    // Implementa la logica del test database
  };

  const handleClearCache = () => {
    console.log('Clear cache');
    // Implementa la logica di pulizia cache
  };

  const handleTestApi = () => {
    console.log('Test API');
    // Implementa la logica del test API
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <CodeIcon color="primary" fontSize="large" />
            <Typography variant="h5">
              Strumenti Sviluppatore
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {!isAdmin ? (
            <Alert 
              severity="warning" 
              icon={<LockIcon />}
              sx={{ mb: 2, mt: 1 }}
            >
              Accesso riservato. Solo gli amministratori possono utilizzare questi strumenti.
            </Alert>
          ) : (
            <>
              <List>
                <ListItem button onClick={handleWebhookTesterOpen}>
                  <ListItemIcon>
                    <WebhookIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Gestione Webhook" 
                    secondary="Configura e gestisci i webhook"
                  />
                </ListItem>

                <ListItem button onClick={handleLocalWebhookTesterOpen}>
                  <ListItemIcon>
                    <WebhookIcon color="secondary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Test Webhook Locale" 
                    secondary="Testa i webhook in ambiente locale"
                  />
                </ListItem>
                
                <ListItem button onClick={handleSimpleWebhookTesterOpen}>
                  <ListItemIcon>
                    <SendIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Test Rapido Webhook" 
                    secondary="Tester semplificato per webhook"
                  />
                </ListItem>
                
                <ListItem button onClick={handleWebhookStatsOpen}>
                  <ListItemIcon>
                    <BarChartIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Statistiche Webhook" 
                    secondary="Visualizza statistiche e tassi di successo"
                  />
                </ListItem>
                
                <Divider />
                
                <ListItem button onClick={handleDatabaseManagementOpen}>
                  <ListItemIcon>
                    <StorageIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Gestione Database" 
                    secondary="Reset dati e importazione da Excel"
                  />
                </ListItem>

                <ListItem button onClick={handleTestDatabase}>
                  <ListItemIcon>
                    <StorageIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Test Database" 
                    secondary="Verifica la connessione al database"
                  />
                </ListItem>

                <ListItem button onClick={handleClearCache}>
                  <ListItemIcon>
                    <RefreshIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Pulisci Cache" 
                    secondary="Rimuovi i dati in cache"
                  />
                </ListItem>

                <ListItem button onClick={handleTestApi}>
                  <ListItemIcon>
                    <CodeIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Test API" 
                    secondary="Verifica lo stato delle API"
                  />
                </ListItem>
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

      <WebhookTester 
        open={webhookTesterOpen} 
        onClose={() => setWebhookTesterOpen(false)} 
        mode="production"
      />

      <WebhookTester 
        open={localWebhookTesterOpen} 
        onClose={() => setLocalWebhookTesterOpen(false)} 
        mode="local"
      />
      
      <Dialog
        open={webhookStatsOpen}
        onClose={() => setWebhookStatsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <BarChartIcon />
            <Typography variant="h6">Statistiche Webhook</Typography>
            <IconButton 
              onClick={() => setWebhookStatsOpen(false)}
              sx={{ marginLeft: 'auto' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <WebhookStatsDashboard />
        </DialogContent>
      </Dialog>
      
      <Dialog
        open={simpleWebhookTesterOpen}
        onClose={() => setSimpleWebhookTesterOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <SendIcon />
            <Typography variant="h6">Test Rapido Webhook</Typography>
            <IconButton 
              onClick={() => setSimpleWebhookTesterOpen(false)}
              sx={{ marginLeft: 'auto' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <SimpleWebhookTester />
        </DialogContent>
      </Dialog>
      
      <Dialog
        open={databaseManagementOpen}
        onClose={() => setDatabaseManagementOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <StorageIcon />
            <Typography variant="h6">Gestione Database</Typography>
            <IconButton 
              onClick={() => setDatabaseManagementOpen(false)}
              sx={{ marginLeft: 'auto' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <DatabaseManagement />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DeveloperOptions; 